import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@/components/feature/Icon";
import { useSiteSettings } from "@/lib/siteSettings";

const navItems = [
  { label: "首頁", path: "/" },
  { label: "關於協會", path: "/about" },
  { label: "會員專區", path: "/membership" },
  { label: "活動與消息", path: "/events" },
  { label: "資源中心", path: "/resources" },
  { label: "聯絡我們", path: "/contact" },
];

/** Site-wide announcement strip, managed in the admin backend (網站設定 → 公告列). */
function AnnouncementBar() {
  const { settings } = useSiteSettings();
  const { announcement } = settings;
  const storageKey = `taiaa_announcement_dismissed:${announcement.text}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  if (!announcement.enabled || !announcement.text.trim() || dismissed) return null;

  const link = announcement.link_url.trim();
  const label = announcement.link_label.trim() || "了解更多";
  const isExternal = /^https?:\/\//i.test(link);

  return (
    <div className="bg-navy-900 text-white" data-testid="announcement-bar">
      <div className="w-full px-6 md:px-10 lg:px-16 py-2 flex items-center justify-center gap-3 text-sm">
        <Icon className="ri-megaphone-line shrink-0 text-silver-300" />
        <span className="truncate">{announcement.text}</span>
        {link &&
          (isExternal ? (
            <a href={link} target="_blank" rel="noopener noreferrer" className="shrink-0 underline underline-offset-2 hover:text-silver-200">
              {label}
            </a>
          ) : (
            <Link to={link} className="shrink-0 underline underline-offset-2 hover:text-silver-200">
              {label}
            </Link>
          ))}
        <button
          type="button"
          aria-label="關閉公告"
          className="ml-2 shrink-0 text-white/60 hover:text-white"
          onClick={() => {
            try {
              sessionStorage.setItem(storageKey, "1");
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
        >
          <Icon className="ri-close-line" />
        </button>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || !isHome
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-colors duration-300 ${
              isScrolled || !isHome ? "bg-navy-950" : "bg-white/10 backdrop-blur-sm border border-white/20"
            }`}>
              <span className={`font-bold text-sm md:text-base tracking-tight transition-colors duration-300 ${
                isScrolled || !isHome ? "text-white" : "text-white"
              }`}>T</span>
            </div>
            <div className="hidden sm:block">
              <p className={`font-bold text-sm md:text-base tracking-wide transition-colors duration-300 ${
                isScrolled || !isHome ? "text-navy-950" : "text-white"
              }`}>
                TAIAA
              </p>
              <p className={`text-[10px] md:text-xs tracking-wider transition-colors duration-300 ${
                isScrolled || !isHome ? "text-navy-700" : "text-white/70"
              }`}>
                台灣 AI 應用推廣協會
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative text-sm font-medium tracking-wide transition-colors whitespace-nowrap py-1 group ${
                    isActive
                      ? isScrolled || !isHome ? "text-navy-950" : "text-white"
                      : isScrolled || !isHome ? "text-navy-800 hover:text-navy-950" : "text-white/80 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-[2px] rounded-full transition-all duration-300 ${
                    isActive ? "w-full" : "w-0 group-hover:w-full"
                  } ${isScrolled || !isHome ? "bg-navy-950" : "bg-white"}`} />
                </Link>
              );
            })}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${
              isScrolled || !isHome ? "text-navy-950" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            <Icon
              className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-2xl`}
            />
          </button>
        </div>
      </div>

      <AnnouncementBar />

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden backdrop-blur-md border-t shadow-lg ${
          isScrolled || !isHome
            ? "bg-white border-silver-200"
            : "bg-navy-950/95 border-white/10"
        }`}>
          <div className="px-6 py-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-3 text-sm font-medium tracking-wide transition-colors ${
                    isActive
                      ? isScrolled || !isHome
                        ? "text-navy-950 font-semibold"
                        : "text-white font-semibold"
                      : isScrolled || !isHome
                        ? "text-navy-800 hover:text-navy-950"
                        : "text-white/80 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}